/**
 * Diaper Models store using Zustand
 * Manages diaper model data, capacity calculations, and leak thresholds
 */

import { create } from 'zustand';
import { loadDiaperModels as apiLoadDiaperModels } from '../lib/api';
import { DiaperModel } from '../lib/types';

interface DiaperState {
  diaperModels: DiaperModel[];
  localModelOverrides: DiaperModel[];
  isLoading: boolean;
  error: string | null;
  /** novaAccountId the cached `diaperModels` were fetched for. */
  loadedAccountId: string | null;

  // Actions
  /**
   * Fetch the Nova diaper-models catalog for the given account. The catalog is
   * effectively per Nova install, but the proxy is account-aware (credentials)
   * so the caller must supply an account. Cached by account id; calling with
   * the same account a second time is a no-op once loaded.
   */
  loadDiaperModels: (novaAccountId: string) => Promise<void>;
  getDiaperModel: (code: number) => DiaperModel | null;
  saveLocalOverride: (model: DiaperModel) => void;
  deleteLocalOverride: (code: number) => void;
}

const LOCAL_OVERRIDES_KEY = 'nova_diaper_model_overrides';

const loadLocalOverrides = (): DiaperModel[] => {
  try {
    const saved = localStorage.getItem(LOCAL_OVERRIDES_KEY);
    return saved ? JSON.parse(saved) : [];
  } catch (e) {
    console.error('Failed to load local diaper model overrides', e);
    return [];
  }
};

export const useDiaperStore = create<DiaperState>((set, get) => ({
  diaperModels: [],
  localModelOverrides: loadLocalOverrides(),
  isLoading: false,
  error: null,
  loadedAccountId: null,

  loadDiaperModels: async (novaAccountId: string) => {
    if (!novaAccountId) {
      throw new Error('loadDiaperModels requires a novaAccountId');
    }
    const { loadedAccountId, diaperModels, isLoading } = get();
    if (isLoading) return;
    if (loadedAccountId === novaAccountId && diaperModels.length > 0) return;

    set({ isLoading: true, error: null });
    try {
      const models = await apiLoadDiaperModels({ novaAccountId });
      const modelsArray: DiaperModel[] = models.map((model: any) => ({
        code: model.code,
        name: model.name,
        capacity: model.capacity,
        c0: model.coefficient0,
        c1: model.coefficient1,
        c2: model.coefficient2,
        c3: model.coefficient3,
        leakThreshold: model.leakThreshold,
        wetnessPolynomial: {
          c0: model.coefficient0,
          c1: model.coefficient1,
          c2: model.coefficient2,
          c3: model.coefficient3,
        },
      }));

      set({ diaperModels: modelsArray, isLoading: false, loadedAccountId: novaAccountId });
    } catch (error: any) {
      set({
        error: error.message || 'Failed to load diaper models',
        isLoading: false,
      });
      console.error('Failed to load diaper models:', error);
      throw error;
    }
  },

  getDiaperModel: (code: number) => {
    const { diaperModels, localModelOverrides } = get();
    // Check local overrides first
    const override = localModelOverrides.find((m) => m.code === code);
    if (override) return override;
    
    return diaperModels.find((m) => m.code === code) || null;
  },

  saveLocalOverride: (model: DiaperModel) => {
    set((state) => {
      const exists = state.localModelOverrides.some(m => m.code === model.code);
      let newOverrides;
      if (exists) {
        newOverrides = state.localModelOverrides.map(m => m.code === model.code ? model : m);
      } else {
        newOverrides = [...state.localModelOverrides, model];
      }
      
      localStorage.setItem(LOCAL_OVERRIDES_KEY, JSON.stringify(newOverrides));
      return { localModelOverrides: newOverrides };
    });
  },

  deleteLocalOverride: (code: number) => {
    set((state) => {
      const newOverrides = state.localModelOverrides.filter(m => m.code !== code);
      localStorage.setItem(LOCAL_OVERRIDES_KEY, JSON.stringify(newOverrides));
      return { localModelOverrides: newOverrides };
    });
  },
}));







